import React, { useCallback, useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { FiCheck, FiDownload, FiX } from "react-icons/fi";
import { useExportRender } from "../../context/ExportRenderContext";
import { usePrecosVisiveis } from "../../context/PrecosContext";
import {
  FORMATOS_EXPORTACAO,
  lerDensidadeAtual,
  montarTemaExportAtual,
  salvarCSV,
  salvarExcel,
  salvarImagem,
  salvarPDF,
} from "../../utils/exportacao";
import {
  AvisoBox,
  BotaoCancelar,
  BotaoExport,
  BotaoFechar,
  BotaoGerar,
  Checkbox,
  ErroBox,
  FormatoItem,
  FormatoList,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSub,
  ModalTitulo,
  OpcaoItem,
  OpcoesList,
  Overlay,
  SectionLabel,
  Toggle,
  ToggleSwitch,
} from "./styles";

const FADE = { duration: 0.18 };

function temDadosTabulares(secoes) {
  // Considera "ha dados tabulares" se EXISTEM colunas declaradas, mesmo que
  // rows esteja vazio. Isso permite exportar PDF/Excel/CSV com cabecalho mas
  // sem registros (caso mais util em paineis filtrados que momentaneamente
  // retornam 0 linhas, como Revisao quando todos os ambientes ja avancaram).
  return Array.isArray(secoes) && secoes.some(
    (s) => s && Array.isArray(s.columns) && s.columns.length > 0
  );
}

function temVisuais(visual) {
  return Boolean(visual && visual.sections && visual.sections.length > 0 && visual.ref);
}

function temAlgumPreco({ resumo = [], secoes = [] }) {
  if (resumo.some((r) => r?.type === "currency")) return true;
  for (const s of secoes) {
    if ((s?.columns || []).some((c) => c?.type === "currency")) return true;
  }
  return false;
}

function filtrarPrecos({ resumo = [], secoes = [] }) {
  const resumoFiltrado = resumo.filter((r) => r?.type !== "currency");
  const secoesFiltradas = secoes.map((s) => ({
    ...s,
    columns: (s.columns || []).filter((c) => c?.type !== "currency"),
  }));
  return { resumo: resumoFiltrado, secoes: secoesFiltradas };
}

/**
 * Componente unificado de exportação.
 *
 * Props:
 *   - titulo (string): nome amigável da exportação (vai no arquivo).
 *   - resumo (Array<{label, value, type?}>): KPIs do topo.
 *   - secoes (Array<{ id, label, columns, rows }>): dados estruturados.
 *   - visual (Object): { ref: pageRef, sections: [{ id, label, selector }] }
 *   - filtrosAtivos (string[]): vai no rodapé do PDF.
 *   - label (string): rótulo do botão. Default: "Exportar painel".
 */
export default function BotaoExportacao({
  titulo,
  resumo = [],
  secoes = [],
  visual = null,
  filtrosAtivos = [],
  label = "Exportar painel",
}) {
  const { setRenderAll } = useExportRender();
  const precosCtx = usePrecosVisiveis();
  const precosVisiveisUI = precosCtx?.precosVisiveis ?? true;

  // Tema atual do usuário ([data-theme] + [data-accent]) resolvido para HEX
  // concreto — html2canvas, jsPDF e o canvas 2D não entendem var(). O helper
  // compartilhado (também usado pelo agendador) resolve no momento do export,
  // incluindo o accent escolhido em Configurações e a fonte do DS.
  const montarTemaExport = useCallback(() => montarTemaExportAtual(), []);
  const [modalAberto, setModalAberto] = useState(false);
  const [formato, setFormato] = useState("pdf");
  const [secoesSel, setSecoesSel] = useState(() =>
    new Set(secoes.map((s) => s.id))
  );
  const [visuaisSel, setVisuaisSel] = useState(() =>
    new Set((visual?.sections || []).map((s) => s.id))
  );
  const [margem, setMargem] = useState(true);
  // Pré-marca "Ocultar preços" se o usuário já está com preços escondidos na UI.
  const [ocultarPrecos, setOcultarPrecos] = useState(!precosVisiveisUI);
  const [erro, setErro] = useState(null);
  const [exportando, setExportando] = useState(false);

  const formatosDisponiveis = useMemo(() => {
    const out = [];
    const haveData = temDadosTabulares(secoes);
    const haveVisual = temVisuais(visual);
    for (const f of FORMATOS_EXPORTACAO) {
      if ((f.id === "pdf" || f.id === "excel" || f.id === "csv") && haveData) {
        out.push(f);
      }
      if ((f.id === "png" || f.id === "jpg") && haveVisual) {
        out.push(f);
      }
    }
    return out;
  }, [secoes, visual]);

  const possuiPrecos = useMemo(() => temAlgumPreco({ resumo, secoes }), [resumo, secoes]);

  // Se o formato atual sumiu (ex.: página só tem visual), realinha.
  useMemo(() => {
    if (!formatosDisponiveis.find((f) => f.id === formato)) {
      const primeiro = formatosDisponiveis[0];
      if (primeiro) setFormato(primeiro.id);
    }
  }, [formatosDisponiveis, formato]);

  function abrir() {
    setErro(null);
    setModalAberto(true);
  }
  function fechar() {
    if (exportando) return;
    setModalAberto(false);
  }

  function toggleSecao(id) {
    setSecoesSel((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }
  function toggleVisual(id) {
    setVisuaisSel((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }

  const usaDados = formato === "pdf" || formato === "excel" || formato === "csv";
  const usaVisual = formato === "png" || formato === "jpg";

  // Todas as seções selecionadas estão sem linhas? O export segue permitido
  // (cabeçalho ainda é útil), mas o usuário precisa saber que o arquivo
  // sairá sem dados de tabela — antes o único indício era o "0 linhas".
  const secoesSelecionadasSemLinhas = useMemo(() => {
    if (!usaDados) return false;
    const selecionadas = secoes.filter((s) => secoesSel.has(s.id));
    return (
      selecionadas.length > 0 &&
      selecionadas.every((s) => !s.rows || s.rows.length === 0)
    );
  }, [usaDados, secoes, secoesSel]);

  async function executar() {
    setErro(null);
    setExportando(true);
    try {
      // Resolve as cores do tema (var(--*) → HEX) agora, com o DOM/tema atual.
      const temaExport = montarTemaExport();
      if (usaDados) {
        const secoesFiltradas = secoes.filter((s) => secoesSel.has(s.id));
        if (secoesFiltradas.length === 0) {
          throw new Error("Selecione ao menos uma seção de dados.");
        }
        const baseResumo = resumo;
        const basePayload = { resumo: baseResumo, secoes: secoesFiltradas };
        const aplicado = ocultarPrecos ? filtrarPrecos(basePayload) : basePayload;
        const payload = {
          titulo,
          resumo: aplicado.resumo,
          secoes: aplicado.secoes,
          filtros: filtrosAtivos,
          ocultarPrecos,
          tema: temaExport,
          // Densidade das Configurações → padding/fonte das tabelas do PDF.
          densidade: lerDensidadeAtual(),
        };
        if (formato === "pdf") salvarPDF(payload);
        else if (formato === "excel") salvarExcel(payload);
        else if (formato === "csv") salvarCSV(payload);
      } else if (usaVisual) {
        const sectionsToCapture = (visual.sections || []).filter((s) =>
          visuaisSel.has(s.id)
        );
        if (sectionsToCapture.length === 0) {
          throw new Error("Selecione ao menos uma seção visual.");
        }
        const root = visual.ref?.current;
        if (!root) {
          throw new Error("Não consegui acessar a área visual da página. Tente recarregar e abrir o modal novamente.");
        }
        const elements = sectionsToCapture
          .map((s) => root.querySelector(s.selector))
          .filter(Boolean);
        if (elements.length === 0) {
          throw new Error("Não encontrei os elementos para capturar.");
        }
        const padding = margem ? 40 : 0;
        await salvarImagem({
          titulo,
          elements,
          formato: formato === "jpg" ? "jpg" : "png",
          padding,
          tema: temaExport,
          expandHooks: {
            beforeCapture: async () => setRenderAll(true),
            afterCapture: async () => setRenderAll(false),
          },
        });
      }
      setModalAberto(false);
    } catch (err) {
      console.error("[Exportação] Falha:", err);
      setErro(err?.message || "Não foi possível gerar o arquivo.");
    } finally {
      setExportando(false);
    }
  }

  if (formatosDisponiveis.length === 0) {
    // Nada a exportar — não renderiza botão.
    return null;
  }

  return (
    <>
      <BotaoExport type="button" onClick={abrir} aria-label={label}>
        <FiDownload size={14} />
        {label}
      </BotaoExport>

      <AnimatePresence>
        {modalAberto && (
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={FADE}
            onClick={fechar}
          >
            <Modal
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={FADE}
              role="dialog"
              aria-modal="true"
              aria-label={`Opções de exportação — ${titulo}`}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitulo>Exportar: {titulo}</ModalTitulo>
                <BotaoFechar onClick={fechar} aria-label="Fechar">
                  <FiX size={18} />
                </BotaoFechar>
              </ModalHeader>
              <ModalSub>
                Captura todo o conteúdo da página, sem paginação nem scroll.
              </ModalSub>

              <SectionLabel>Formato</SectionLabel>
              <FormatoList>
                {formatosDisponiveis.map((f) => (
                  <FormatoItem
                    key={f.id}
                    type="button"
                    $active={formato === f.id}
                    onClick={() => setFormato(f.id)}
                  >
                    <strong>{f.label}</strong>
                    <span>{f.descricao}</span>
                  </FormatoItem>
                ))}
              </FormatoList>

              {usaDados && secoes.length > 0 && (
                <>
                  <SectionLabel>Seções de dados</SectionLabel>
                  <OpcoesList>
                    {secoes.map((s) => (
                      <OpcaoItem key={s.id}>
                        <Checkbox $active={secoesSel.has(s.id)}>
                          {secoesSel.has(s.id) && <FiCheck size={13} />}
                        </Checkbox>
                        <input
                          type="checkbox"
                          hidden
                          checked={secoesSel.has(s.id)}
                          onChange={() => toggleSecao(s.id)}
                        />
                        <span onClick={() => toggleSecao(s.id)}>{s.label}</span>
                        {s.rows && (
                          <small>
                            {s.rows.length} linha{s.rows.length !== 1 ? "s" : ""}
                          </small>
                        )}
                      </OpcaoItem>
                    ))}
                  </OpcoesList>
                </>
              )}

              {usaVisual && (visual?.sections?.length || 0) > 0 && (
                <>
                  <SectionLabel>Seções visuais</SectionLabel>
                  <OpcoesList>
                    {visual.sections.map((s) => (
                      <OpcaoItem key={s.id}>
                        <Checkbox $active={visuaisSel.has(s.id)}>
                          {visuaisSel.has(s.id) && <FiCheck size={13} />}
                        </Checkbox>
                        <input
                          type="checkbox"
                          hidden
                          checked={visuaisSel.has(s.id)}
                          onChange={() => toggleVisual(s.id)}
                        />
                        <span onClick={() => toggleVisual(s.id)}>{s.label}</span>
                      </OpcaoItem>
                    ))}
                  </OpcoesList>
                  <Toggle>
                    <ToggleSwitch $on={margem} />
                    <input
                      type="checkbox"
                      hidden
                      checked={margem}
                      onChange={(e) => setMargem(e.target.checked)}
                    />
                    <span onClick={() => setMargem((v) => !v)}>
                      Margem ao redor da imagem
                    </span>
                  </Toggle>
                </>
              )}

              {usaDados && possuiPrecos && (
                <>
                  <SectionLabel>Privacidade</SectionLabel>
                  <Toggle>
                    <ToggleSwitch $on={ocultarPrecos} />
                    <input
                      type="checkbox"
                      hidden
                      checked={ocultarPrecos}
                      onChange={(e) => setOcultarPrecos(e.target.checked)}
                    />
                    <span onClick={() => setOcultarPrecos((v) => !v)}>
                      Ocultar preços e valores monetários no arquivo
                    </span>
                  </Toggle>
                </>
              )}

              {secoesSelecionadasSemLinhas && (
                <AvisoBox>
                  As seções selecionadas estão sem linhas no momento — o
                  arquivo sairá apenas com cabeçalhos{formato === "excel" ? " e a aba Resumo" : ""}.
                </AvisoBox>
              )}

              {erro && <ErroBox>{erro}</ErroBox>}

              <ModalFooter>
                <BotaoCancelar onClick={fechar} disabled={exportando}>
                  Cancelar
                </BotaoCancelar>
                <BotaoGerar onClick={executar} disabled={exportando}>
                  <FiDownload size={14} />
                  {exportando ? "Gerando..." : "Exportar"}
                </BotaoGerar>
              </ModalFooter>
            </Modal>
          </Overlay>
        )}
      </AnimatePresence>
    </>
  );
}
